import { useState } from "react";
import { api } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Layers, Loader2, Sparkles, CheckCircle2, XCircle, Trash2 } from "lucide-react";
import { CategoryField } from "@/components/entity/CategoryField";

const CURRENCIES = ["INR", "USD", "EUR", "GBP"];

async function parseOne(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/invoices/parse-async", fd);
  let job = data;
  const started = Date.now();
  while (job.status === "processing" && Date.now() - started < 300000) {
    await new Promise((r) => setTimeout(r, 2000));
    const res = await api.get(`/invoices/parse-jobs/${data.job_id}`);
    job = { ...res.data, job_id: data.job_id };
  }
  if (job.status !== "done") throw new Error(job.error || "Parsing failed");
  return job.invoice || {};
}

async function storeAttachment(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/attachments", fd);
  return data; // {id, filename, content_type, size}
}

export function BulkInvoiceUploader({ onPosted }) {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [stage, setStage] = useState("pick"); // pick | extracting | review | saving | done
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [items, setItems] = useState([]);
  const [result, setResult] = useState(null);

  const reset = () => {
    setFiles([]);
    setStage("pick");
    setItems([]);
    setProgress({ done: 0, total: 0 });
    setResult(null);
  };

  const extract = async () => {
    if (!files.length) return toast.error("Choose one or more invoice files");
    setStage("extracting");
    setProgress({ done: 0, total: files.length });
    const collected = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      const row = { key: `${i}-${f.name}`, filename: f.name, status: "ok", error: null, attachment: null, fields: {} };
      try {
        const [att, fields] = await Promise.all([storeAttachment(f), parseOne(f)]);
        row.attachment = att;
        row.fields = {
          vendor: fields.vendor || "",
          invoice_no: fields.invoice_no || "",
          invoice_date: fields.invoice_date || "",
          due_date: fields.due_date || "",
          amount: fields.amount || 0,
          sub_total: fields.sub_total || 0,
          gst_amount: fields.gst_amount || 0,
          currency: fields.currency || "INR",
          category: fields.category || "",
          gstin: fields.gstin || "",
          pan: fields.pan || "",
          tds_rate: fields.tds_rate || 0,
          tds_section: fields.tds_section || "",
          description: fields.description || "",
        };
      } catch (e) {
        row.status = "error";
        row.error = e.response?.data?.detail || e.message || "Could not read invoice";
      }
      collected.push(row);
      setProgress({ done: i + 1, total: files.length });
    }
    setItems(collected);
    setStage("review");
    const bad = collected.filter((r) => r.status === "error").length;
    if (bad) toast.warning(`${bad} file(s) could not be read — they are skipped`);
  };

  const setField = (key, k, v) =>
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, fields: { ...it.fields, [k]: v } } : it)));

  const removeItem = (key) => setItems((prev) => prev.filter((it) => it.key !== key));

  const saveAll = async () => {
    const valid = items.filter((it) => it.status === "ok" && it.fields.vendor && Number(it.fields.amount) > 0);
    if (!valid.length) return toast.error("Each invoice needs a vendor and an amount > 0");
    setStage("saving");
    let saved = 0;
    const fails = [];
    for (const it of valid) {
      try {
        const base = Number(it.fields.sub_total) > 0 ? Number(it.fields.sub_total) : Number(it.fields.amount || 0); // TDS on taxable value before GST
        const tds_amount = Math.round(base * (Number(it.fields.tds_rate) || 0)) / 100;
        await api.post("/invoices/post-as-payable", {
          ...it.fields,
          tds_amount,
          attachments: it.attachment ? [it.attachment] : [],
        });
        saved += 1;
      } catch (e) {
        fails.push({ filename: it.filename, error: e.response?.data?.detail || "Save failed" });
      }
    }
    setResult({ saved, fails });
    setStage("done");
    if (saved) {
      toast.success(`${saved} payable(s) created`);
      onPosted && onPosted();
    }
    if (fails.length) toast.error(`${fails.length} invoice(s) failed to save`);
  };

  const okCount = items.filter((i) => i.status === "ok").length;

  return (
    <>
      <Button onClick={() => setOpen(true)} data-testid="open-bulk-upload-btn" variant="outline" className="gap-2">
        <Layers className="h-4 w-4" /> Bulk Upload
      </Button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Bulk Upload Invoices</DialogTitle>
            <DialogDescription>
              {stage === "pick" && "Select multiple invoice PDFs or images."}
              {stage === "extracting" && `AI extracting ${progress.done}/${progress.total}…`}
              {stage === "review" && "Review and edit each invoice, then save them all as Payables."}
              {stage === "saving" && "Saving payables…"}
              {stage === "done" && "Done."}
            </DialogDescription>
          </DialogHeader>

          {stage === "pick" && (
            <div className="py-2">
              <Label className="text-xs mb-1.5 block">Invoice files (PDF / image)</Label>
              <Input
                type="file"
                multiple
                accept=".pdf,.png,.jpg,.jpeg,.webp"
                onChange={(e) => setFiles(Array.from(e.target.files || []))}
                data-testid="bulk-file-input"
              />
              {files.length > 0 && (
                <p className="text-xs text-muted-foreground mt-2">{files.length} file(s) selected.</p>
              )}
            </div>
          )}

          {stage === "extracting" && (
            <div className="py-8 flex flex-col items-center gap-3" data-testid="bulk-extracting">
              <Loader2 className="h-8 w-8 animate-spin text-brand" />
              <p className="text-sm text-muted-foreground">Extracting {progress.done} of {progress.total}…</p>
            </div>
          )}

          {stage === "review" && (
            <div className="py-1 space-y-2" data-testid="bulk-review-panel">
              {items.map((it) => (
                <div
                  key={it.key}
                  className={`rounded-md border p-3 ${it.status === "error" ? "border-danger/40 bg-danger/5" : "border-border"}`}
                  data-testid="bulk-review-row"
                >
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="text-xs font-medium truncate flex items-center gap-1.5">
                      {it.status === "error" ? <XCircle className="h-3.5 w-3.5 text-danger" /> : <CheckCircle2 className="h-3.5 w-3.5 text-success" />}
                      {it.filename}
                    </span>
                    <button onClick={() => removeItem(it.key)} className="text-muted-foreground hover:text-danger p-1" title="Remove" data-testid="bulk-remove-row">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  {it.status === "error" ? (
                    <p className="text-xs text-danger">{it.error}</p>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <Input className="h-8 text-xs" placeholder="Vendor" value={it.fields.vendor} onChange={(e) => setField(it.key, "vendor", e.target.value)} data-testid="bulk-vendor" />
                      <Input className="h-8 text-xs" placeholder="Invoice No." value={it.fields.invoice_no} onChange={(e) => setField(it.key, "invoice_no", e.target.value)} data-testid="bulk-invoice" />
                      <div className="h-8"><CategoryField catType="expense" value={it.fields.category} onChange={(v) => setField(it.key, "category", v)} testid={`bulk-category-${it.key}`} /></div>
                      <Input type="number" step="0.01" className="h-8 text-xs" placeholder="Amount" value={it.fields.amount} onChange={(e) => setField(it.key, "amount", Number(e.target.value))} data-testid="bulk-amount" />
                      <Input type="date" className="h-8 text-xs" value={it.fields.invoice_date} onChange={(e) => setField(it.key, "invoice_date", e.target.value)} data-testid="bulk-date" />
                      <Input type="date" className="h-8 text-xs" value={it.fields.due_date} onChange={(e) => setField(it.key, "due_date", e.target.value)} data-testid="bulk-due" />
                      <select className="h-8 text-xs rounded-md bg-background border border-input px-2" value={it.fields.currency} onChange={(e) => setField(it.key, "currency", e.target.value)} data-testid="bulk-currency">
                        {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <Input type="number" step="0.01" className="h-8 text-xs" placeholder="TDS %" value={it.fields.tds_rate || 0} onChange={(e) => setField(it.key, "tds_rate", Number(e.target.value))} data-testid="bulk-tds-rate" title="TDS rate %" />
                      <div className="flex items-center justify-end text-xs font-mono text-muted-foreground" data-testid="bulk-row-total">
                        {Number(it.fields.tds_rate) > 0
                          ? `TDS ${formatMoney(Math.round((Number(it.fields.sub_total) > 0 ? Number(it.fields.sub_total) : Number(it.fields.amount || 0)) * Number(it.fields.tds_rate)) / 100, it.fields.currency)}`
                          : formatMoney(it.fields.amount || 0, it.fields.currency)}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {stage === "saving" && (
            <div className="py-8 flex flex-col items-center gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-brand" />
              <p className="text-sm text-muted-foreground">Saving payables…</p>
            </div>
          )}

          {stage === "done" && result && (
            <div className="py-6 space-y-3" data-testid="bulk-done-panel">
              <p className="text-sm flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-success" /> {result.saved} payable(s) created.</p>
              {result.fails.length > 0 && (
                <div className="text-xs text-danger space-y-1">
                  {result.fails.map((f, i) => <p key={i}>• {f.filename}: {f.error}</p>)}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {stage === "pick" && (
              <>
                <Button variant="outline" onClick={() => { setOpen(false); reset(); }}>Cancel</Button>
                <Button onClick={extract} disabled={!files.length} data-testid="bulk-extract-btn" className="gap-2">
                  <Sparkles className="h-4 w-4" /> Extract {files.length || ""} invoice(s)
                </Button>
              </>
            )}
            {stage === "review" && (
              <>
                <Button variant="outline" onClick={reset}>Start over</Button>
                <Button onClick={saveAll} disabled={okCount === 0} data-testid="bulk-save-all-btn" className="gap-2">
                  <CheckCircle2 className="h-4 w-4" /> Save {okCount} as Payables
                </Button>
              </>
            )}
            {stage === "done" && (
              <Button onClick={() => { setOpen(false); reset(); }} data-testid="bulk-close-btn">Close</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
